import React, { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useAuth } from "@/context/AuthContext";
import { resendVerification } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Milk, Loader2, MailCheck } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

function errMsg(detail, fallback) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return fallback;
}

export default function Signup() {
  const { signUp } = useAuth();
  const { t } = useT();
  const [form, setForm] = useState({ company_name: "", admin_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [sentTo, setSentTo] = useState("");
  const [resending, setResending] = useState(false);
  const recaptchaRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (RECAPTCHA_SITE_KEY && !captchaToken) {
      setError(t("signup.captchaRequired"));
      return;
    }
    setLoading(true);
    try {
      const email = form.email.trim().toLowerCase();
      await signUp({ ...form, email, recaptcha_token: captchaToken });
      setSentTo(email);
    } catch (err) {
      setError(errMsg(err.response?.data?.detail, t("signup.error")) || err.message);
      if (recaptchaRef.current) { recaptchaRef.current.reset(); setCaptchaToken(null); }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await resendVerification(sentTo);
      toast.success(t("signup.resent"));
    } catch {
      toast.error(t("signup.resendFailed"));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center header-band px-4 py-10">
      <div className="absolute right-4 top-4"><LanguageSwitcher /></div>
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--brand))] text-white shadow-lg">
            <Milk className="h-7 w-7" />
          </div>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">{t("signup.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("signup.subtitle")}</p>
        </div>
        <Card>
          <CardContent className="p-6">
            {sentTo ? (
              <div className="flex flex-col items-center text-center" data-testid="signup-verify-sent">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]">
                  <MailCheck className="h-6 w-6" />
                </div>
                <h2 className="mt-4 text-lg font-semibold">{t("signup.verifyTitle")}</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t("signup.verifySent")} <span className="font-semibold text-foreground">{sentTo}</span>
                </p>
                <p className="mt-2 text-xs text-muted-foreground">{t("signup.verifyHint")}</p>
                <Button variant="outline" className="mt-4 w-full" onClick={resend} disabled={resending} data-testid="signup-resend-button">
                  {resending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {t("signup.resend")}
                </Button>
                <a href="/login" className="mt-4 text-sm font-semibold text-[hsl(var(--brand))] hover:underline">{t("signup.backToLogin")}</a>
              </div>
            ) : (
              <>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="company">{t("signup.company")}</Label>
                    <Input id="company" value={form.company_name} onChange={set("company_name")} data-testid="signup-company-input" required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="admin">{t("signup.adminName")}</Label>
                    <Input id="admin" value={form.admin_name} onChange={set("admin_name")} data-testid="signup-admin-input" required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email">{t("signup.email")}</Label>
                    <Input id="email" type="email" value={form.email} onChange={set("email")} data-testid="signup-email-input" required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password">{t("signup.password")}</Label>
                    <Input id="password" type="password" value={form.password} onChange={set("password")} data-testid="signup-password-input" minLength={6} required />
                  </div>
                  {RECAPTCHA_SITE_KEY ? (
                    <div className="flex justify-center" data-testid="signup-recaptcha">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(tok) => setCaptchaToken(tok)}
                        onExpired={() => setCaptchaToken(null)}
                      />
                    </div>
                  ) : null}
                  {error && <div className="rounded-lg badge-bad px-3 py-2 text-sm" data-testid="signup-error">{error}</div>}
                  <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-button">
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {t("signup.submit")}
                  </Button>
                </form>
                <p className="mt-4 text-center text-sm text-muted-foreground">
                  {t("signup.haveAccount")}{" "}
                  <a href="/login" className="font-semibold text-[hsl(var(--brand))] hover:underline">{t("signup.signInCta")}</a>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
